/**
 * In-process fake for unit tests (§4). Not a network mock — deliberately.
 *
 * Everything that needs a *real* network stack (latency, failure, CORS,
 * server clock) is exercised against the real API instead, because a fake could
 * only reproduce what we already assume about those behaviours.
 */
import type { ApiClient, InjectionConfig, ServerTimeSample } from './client'
import { ApiError } from './client'
import type {
  ApiItem,
  CompleteSessionResponse,
  CreateSessionResponse,
  PushSubscriptionInput,
  SessionDescriptor,
} from './contract'

/**
 * A structurally valid base64url uncompressed P-256 point, so the client's
 * `urlBase64ToUint8Array` is genuinely exercised rather than short-circuited by
 * an obviously fake string. It is a real generated key whose private half was
 * discarded — it can never push anything.
 */
const FAKE_VAPID_PUBLIC_KEY =
  'BEnQ5BnoD5yrJmDZCJ3tDJxQ0vJ7l0FQyPLBcFQlGzVRLPBLcJ0zKxSJxjJ8qBmLQ0vFQnJxQ9KxRLBcFQlGzU'

export interface FakeApiOptions {
  descriptors?: SessionDescriptor[]
  items?: ApiItem[]
  /** Offset applied to the fake server clock, to exercise §11 arithmetic in tests. */
  clockSkewMs?: number
}

const defaultDescriptors: SessionDescriptor[] = [
  {
    id: 'drill-verbal-basic',
    kind: 'drill',
    title: 'Verbální oddíl — rozcvička',
    subtitle: '2 úlohy, bez časového limitu',
    itemCount: 2,
    durationSec: null,
  },
]

const defaultItems: ApiItem[] = [
  {
    id: 'i-001',
    version: 1,
    type: 'single-choice',
    groupId: null,
    content: {
      prompt: 'Které slovo nejlépe vystihuje význam slova lakonický?',
      options: [
        { id: 'a', text: 'stručný' },
        { id: 'b', text: 'zdvořilý' },
      ],
    },
    correctAnswer: { optionId: 'a' },
    explanation: { text: 'Lakonický znamená stručný, úsečný.' },
    assetIds: [],
    servedAt: 0,
  },
  {
    id: 'i-002',
    version: 1,
    type: 'single-choice',
    groupId: null,
    content: {
      prompt: 'Doplňte řadu: 2, 6, 12, 20, 30, ?',
      options: [
        { id: 'a', text: '36' },
        { id: 'b', text: '42' },
      ],
    },
    correctAnswer: { optionId: 'b' },
    explanation: { text: 'Rozdíly rostou o 2; 30 + 12 = 42.' },
    assetIds: [],
    servedAt: 0,
  },
]

export function createFakeApi(options: FakeApiOptions = {}): ApiClient {
  const descriptors = options.descriptors ?? defaultDescriptors
  const items = new Map((options.items ?? defaultItems).map((i) => [i.id, { ...i }]))
  const skew = options.clockSkewMs ?? 0

  const sessions = new Map<string, CreateSessionResponse>()
  const recorded = new Map<string, Map<string, { itemId: string; correct: boolean; versionConflict: boolean }>>()

  let injection: InjectionConfig = { latencyMs: 0, failureRate: 0, holdForever: false }
  let lastSample: ServerTimeSample | null = null
  const listeners = new Set<(s: ServerTimeSample) => void>()
  const pushSubscriptions = new Map<string, PushSubscriptionInput>()

  const now = (): number => Date.now() + skew

  function tick(): number {
    const serverTimeMs = now()
    lastSample = { serverTimeMs, receivedAtMs: Date.now() }
    for (const l of listeners) l(lastSample)
    return serverTimeMs
  }

  function maybeFail(): void {
    if (injection.failureRate > 0 && Math.random() < injection.failureRate) {
      throw new ApiError('injected_failure', 503)
    }
  }

  return {
    async loginStub(displayName) {
      maybeFail()
      return {
        accessToken: 'fake-access-token',
        expiresInSec: 900,
        user: { id: 'u-1', displayName: displayName ?? 'Student' },
        servedAt: tick(),
      }
    },

    async getMe() {
      maybeFail()
      return {
        userId: 'u-1',
        displayName: 'Student',
        entitlement: { tier: 'premium', validUntil: now() + 30 * 24 * 3600 * 1000 },
        servedAt: tick(),
      }
    },

    async getPlan() {
      maybeFail()
      return {
        servedAt: tick(),
        headline: 'Nebo si vyber sám',
        recommendation: {
          descriptorId: descriptors[0]?.id ?? '',
          title: 'Nech si sestavit plán na míru',
          body: 'Z krátké diagnostiky ti složíme lekce na to, co ti nejde.',
        },
        descriptors,
      }
    },

    async prepareSession(descriptorId) {
      maybeFail()
      const descriptor = descriptors.find((d) => d.id === descriptorId)
      if (!descriptor) throw new ApiError('unknown_descriptor', 404)
      const servedAt = tick()
      const chosen = [...items.values()].slice(0, descriptor.itemCount).map((i) => ({ ...i, servedAt }))
      return {
        preparationId: `prep-fake-${descriptorId}`,
        descriptorId,
        itemIds: chosen.map((i) => i.id),
        servedAt,
        items: chosen,
      }
    },

    async createSession({ descriptorId, elapsedSecOverride }) {
      maybeFail()
      const descriptor = descriptors.find((d) => d.id === descriptorId)
      if (!descriptor) throw new ApiError('unknown_descriptor', 404)

      const servedAt = tick()
      const chosen = [...items.values()].slice(0, descriptor.itemCount).map((i) => ({ ...i, servedAt }))
      const session: CreateSessionResponse = {
        id: `s-fake-${sessions.size + 1}`,
        kind: descriptor.kind,
        itemIds: chosen.map((i) => i.id),
        startedAt: servedAt - (elapsedSecOverride ?? 0) * 1000,
        durationSec: descriptor.durationSec,
        grantToken: `grant-fake-${sessions.size + 1}`,
        servedAt,
        items: chosen,
      }
      sessions.set(session.id, session)
      recorded.set(session.id, new Map())
      return session
    },

    async getSession(sessionId) {
      maybeFail()
      const session = sessions.get(sessionId)
      if (!session) throw new ApiError('unknown_session', 404)
      const servedAt = tick()
      const seen = recorded.get(sessionId)
      // The grant token is issued once, by createSession; the real endpoint does not repeat it.
      return {
        id: session.id,
        kind: session.kind,
        itemIds: session.itemIds,
        startedAt: session.startedAt,
        durationSec: session.durationSec,
        servedAt,
        status: 'active',
        recordedItemIds: [...new Set([...(seen?.values() ?? [])].map((r) => r.itemId))],
        items: session.items.map((i) => ({ ...items.get(i.id)!, servedAt })),
      }
    },

    async submitResponses({ sessionId, responses }) {
      maybeFail()
      const session = sessions.get(sessionId)
      if (!session) throw new ApiError('unknown_session', 404)
      const seen = recorded.get(sessionId) ?? new Map()
      recorded.set(sessionId, seen)

      const acks = responses.map((r) => {
        if (seen.has(r.id)) return { id: r.id, status: 'duplicate' as const }
        const item = items.get(r.itemId)
        if (!item) return { id: r.id, status: 'rejected' as const, reason: 'unknown_item' }
        const correct = shallowAnswerEquals(r.answer, item.correctAnswer)
        const versionConflict = r.itemVersion !== item.version
        seen.set(r.id, { itemId: r.itemId, correct, versionConflict })
        return {
          id: r.id,
          status: 'accepted' as const,
          correct,
          itemVersionOnServer: item.version,
          versionConflict,
        }
      })

      return { acks, servedAt: tick() }
    },

    async completeSession({ sessionId }): Promise<CompleteSessionResponse> {
      maybeFail()
      const session = sessions.get(sessionId)
      if (!session) throw new ApiError('unknown_session', 404)
      const seen = [...(recorded.get(sessionId)?.values() ?? [])]
      const rawScore = seen.filter((r) => r.correct).length
      const maxScore = session.itemIds.length
      const servedAt = tick()
      return {
        sessionId,
        rawScore,
        maxScore,
        percentile: maxScore === 0 ? 0 : Math.round((rawScore / maxScore) * 96 + 2),
        durationOverrunFlagged: false,
        perItem: seen.map((r) => ({ itemId: r.itemId, correct: r.correct, versionConflict: r.versionConflict })),
        completedAt: servedAt,
        servedAt,
      }
    },

    async bumpItemVersion(itemId) {
      const item = items.get(itemId)
      if (!item) throw new ApiError('unknown_item', 404)
      item.version += 1
      return { itemId, version: item.version, servedAt: tick() }
    },

    async getVapidPublicKey() {
      return { publicKey: FAKE_VAPID_PUBLIC_KEY, servedAt: tick() }
    },

    async registerPushSubscription(input) {
      const handle = input.transport === 'webpush' ? input.endpoint : input.token
      pushSubscriptions.set(handle, input)
      return {
        id: `sub-${handle.slice(-8)}`,
        transport: input.transport,
        target: handle,
        vapidPublicKey: FAKE_VAPID_PUBLIC_KEY,
        count: pushSubscriptions.size,
        servedAt: tick(),
      }
    },

    async unregisterPushSubscription({ endpoint, token }) {
      // Not an error for an unknown handle: the real endpoint is idempotent, and
      // a fake that is stricter than the server teaches the wrong lesson.
      if (endpoint) pushSubscriptions.delete(endpoint)
      if (token) pushSubscriptions.delete(token)
    },

    async sendTestPush({ message, url }) {
      // The fake cannot deliver anything — there is no push service in process,
      // and §4 rules out a Service-Worker-based mock because Service Workers are
      // unavailable in the iOS webview. That is precisely why push delivery is
      // checked by a device smoke test rather than a unit test. What it *can* report is the registration count, which
      // is what distinguishes "nothing registered" from "delivery failed".
      const servedAt = tick()
      return {
        title: 'Sciolingo',
        body: message,
        url: url ?? '/',
        webPushConfigured: true,
        fcmConfigured: false,
        fcmDisabledReason: 'the fake has no push service',
        vapidKeySource: 'fake',
        statePersistable: false,
        subscriptions: pushSubscriptions.size,
        delivered: 0,
        skipped: pushSubscriptions.size,
        failed: 0,
        pruned: 0,
        results: [...pushSubscriptions.values()].map((s) => ({
          transport: s.transport,
          target: s.transport === 'webpush' ? s.endpoint : s.token,
          status: 'skipped',
          detail: 'fake api',
        })),
        servedAt,
      }
    },

    setAccessToken() {
      /* the fake does not authorise anything */
    },

    setInjection(config) {
      injection = config
    },

    lastServerTime() {
      return lastSample
    },

    onServerTime(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
}

function shallowAnswerEquals(a: unknown, b: unknown): boolean {
  return JSON.stringify(normalise(a)) === JSON.stringify(normalise(b))
}

function normalise(value: unknown): unknown {
  if (Array.isArray(value)) return [...value].map(normalise).sort()
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([x], [y]) => x.localeCompare(y))
        .map(([k, v]) => [k, normalise(v)]),
    )
  }
  return value
}
